/**
 * Карточка хозяйства — дом хозяйства (AK-746).
 *
 * Wireframe одобрен founder 25.08 (`docs/superpowers/prototypes/farm-card/`).
 * До неё у хозяйства не было своей страницы: реквизиты жили в drawer'е
 * «Структура», поля — в общем списке с фильтром, архив не показывался нигде.
 *
 * Отдельная СТРАНИЦА, а не расширенный drawer: четыре вкладки с таблицами в
 * панель не влезают, а ссылка на хозяйство должна открываться и пересылаться
 * (то же решение, что для имени поля в AK-750). `FarmDrawer` никуда не делся —
 * он остаётся редактором реквизитов, и карточка открывает его кнопкой.
 *
 * AK-899 (макет v2, founder 08.10): карточка стала разделом «Данные
 * хозяйства» по адресу `/farm/<id>` и вобрала флоу «Подготовка хозяйства»:
 * его шаги — полоса готовности в шапке, матрица истории культур — вкладка.
 * Вкладки «План» и «Экономика» сняты (план — ссылкой из «Обзора»). Старые
 * адреса — редиректы (`pages/farm/index.tsx`). Файлы оставлены на месте:
 * переезд папки не меняет поведения, а историю правок карточки рвёт.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Stack } from '@/components/ui/stack';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TileGrid } from '@/components/ui/tile-grid';
import { PERM, useCan } from '@/lib/auth/usePermissions';
import { ARCHIVE_BATCH_MAX, capBatchSelection } from '@/lib/bulk/batchLimits';
import { useTextFilterDraft } from '@/lib/filters/useTextFilterDraft';
import { invalidateMapInit } from '@/lib/map/mapInitCache';
import { useScopeChanged } from '@/lib/scopeEvent';
import { toast } from '@/lib/toast';
import { FarmCropHistoryMatrix } from '@/pages/farm/history';
import { FarmTwinStrip, type TwinStep } from '@/components/farm/FarmTwinStrip';
import {
  buildTwinView,
  farmTabFromHref,
  isTwinContract,
  lastUpdateText,
  useFarmPreparation,
} from '@/pages/farm/preparation';
import { FarmDrawer } from '../components/FarmDrawer';
import { useClusters, useCompanies, useFarms } from '../hooks/useStructureQueries';
import { ArchiveFieldsDialog } from './ArchiveFieldsDialog';
import { BrigadeDialog, type BrigadeDraft } from './BrigadeDialog';
import { BrigadesTab } from './BrigadesTab';
import { FieldsTab } from './FieldsTab';
import { MoveToBrigadeDialog } from './MoveToBrigadeDialog';
import { OverviewTab, contourNote, formatHa } from './OverviewTab';
import { FarmTrialsBlock } from '@/components/science/TrialsBlocks';
import {
  archiveFields,
  moveFieldsToBrigade,
  restoreFields,
  selectionStats,
} from './bulkActions';
import { FarmMachinerySection } from './machinery/FarmMachinerySection';
import { resolveMachineryView, type MachineryView } from './machinery/machineryLogic';
import { useFleetFreshness, useMachineryCounts } from './machinery/queries';
import { useFarmCardBulk, useFarmFields, useFarmSummary, useSaveBrigade } from './queries';
import type { FarmBrigadeRow, FarmFieldsMode, FarmSummary } from './types';

/**
 * Вкладки верхнего ряда. «План» и «Экономика» сняты (AK-899): старая ссылка
 * с `?tab=plan` / `?tab=economy` не падает, а открывает «Обзор», где ссылка
 * на планирование осталась.
 */
const TABS = ['overview', 'fields', 'brigades', 'machinery', 'people', 'history'] as const;
export type FarmCardTab = (typeof TABS)[number];

export function resolveFarmCardTab(raw: unknown): FarmCardTab {
  return TABS.includes(raw as FarmCardTab) ? (raw as FarmCardTab) : 'overview';
}

/**
 * Подзаголовок шапки: кластер · админ хозяйства · гл. агроном хозяйства
 * (макет v2). Человека нет в сводке — нет и строки: «не назначен» на роли,
 * которой в системе ещё не существует, выглядело бы как недосмотр.
 */
export function farmHeaderLine(
  summary: Pick<FarmSummary, 'cluster_name' | 'farm_admin_name' | 'chief_agronomist_name'>,
): string {
  return [
    summary.cluster_name ? `Кластер «${summary.cluster_name}»` : null,
    summary.farm_admin_name ? `Админ хозяйства — ${summary.farm_admin_name}` : null,
    summary.chief_agronomist_name
      ? `Гл. агроном хозяйства — ${summary.chief_agronomist_name}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Аргументы перехода при смене вкладки. Вынесено из компонента, чтобы
 * проверялось без монтирования всей страницы.
 *
 * `replace: true` — переключение вкладок это осмотр ОДНОГО хозяйства, а не
 * череда переходов: с `push` кнопка «назад» уводила бы человека по всем
 * вкладкам, которые он открывал, вместо возврата туда, откуда он пришёл.
 *
 * «Обзор» уезжает в адрес как ОТСУТСТВИЕ параметра: дефолт в ссылке —
 * визуальный мусор, а `?tab=overview` ещё и раздваивает адрес одной и той же
 * страницы (её можно было бы получить двумя разными URL).
 */
export function farmCardTabNavigation(farmId: string, next: FarmCardTab, subtab?: string) {
  return {
    to: '/farm/$farmId' as const,
    params: { farmId },
    search: {
      tab: next === 'overview' ? undefined : next,
      // Под-вкладка едет в адрес по тому же правилу, что и вкладка: дефолт
      // («Единицы») — это ОТСУТСТВИЕ параметра, иначе у одной страницы два
      // разных адреса. Смена верхней вкладки под-вкладку не тащит.
      subtab: subtab || undefined,
    },
    replace: true,
  };
}

export function FarmCardPage({
  farmId,
  tab,
  subtab,
  issue: issueFromUrl,
  action,
}: {
  farmId: string;
  tab?: string;
  subtab?: string;
  /**
   * Действие, с которым карточку ОТКРЫЛИ ссылкой (AK-804: `action=import` —
   * шаг «Техника» подготовки хозяйства ведёт сразу в мастер загрузки списком).
   * Дальше живёт в состоянии диалога: адрес переписывать на каждое открытие
   * окна незачем.
   */
  action?: string;
  /**
   * Сигнал «здоровья данных», с которым карточку ОТКРЫЛИ извне (AK-772).
   *
   * Подготовка хозяйства ссылается на причину («9 полей без контура →
   * разобрать»), и обещание «ссылка открывает ровно эти девять» держится
   * только если фильтр приезжает в адресе. Дальше он живёт в состоянии: клик
   * по другому сигналу внутри карточки адрес не переписывает — это осмотр
   * одной страницы, а не череда переходов.
   */
  issue?: string;
}) {
  const navigate = useNavigate();
  const canWrite = useCan(PERM.fieldWrite, 'u');
  // Перемещение в отделение бэк пока гейтит tenant-level `structure` (AK-930).
  const canMoveToBrigade = useCan(PERM.fieldMoveToBrigade, 'u');
  // Парк техники бэкенд гейтит на `company_reference`, а не на `structure`:
  // право править поля и право вести справочники компании — разные вещи, и
  // показывать кнопку тому, кому сервер ответит 403, нельзя.
  const canManageMachinery = useCan(PERM.machineryWrite, 'u');

  const summaryQuery = useFarmSummary(farmId);
  const summary = summaryQuery.data;
  // Счётчик техники приходит СВОИМ запросом: сводка карточки его не знает, а
  // считать по загруженной странице списка нельзя — бейдж говорит про парк
  // хозяйства целиком.
  const machineryCounts = useMachineryCounts(farmId);

  // Полоса двойника (AK-900) — шесть шагов ОДНОЙ ручки подготовки: цифры
  // шагов считает сервер по тем же условиям, что цифры вкладок, поэтому они
  // не могут разойтись. Парк и его актуальность с клиента нужны только как
  // fallback для старого бэкенда (3+1 шага) — на новом договоре актуальность
  // парка полоса не спрашивает.
  const prepQuery = useFarmPreparation(farmId);
  const legacyPrep = !!prepQuery.data && !isTwinContract(prepQuery.data);
  const freshnessQuery = useFleetFreshness(farmId, legacyPrep);
  const twin = buildTwinView({
    prep: prepQuery.data,
    counts: machineryCounts.data,
    freshness: freshnessQuery.data,
    // Отказ парка — «сказать нечем», а не «плохо» (право справочников).
    machineryDenied: machineryCounts.isError,
  });

  // Раздел адресуется хозяйством, но человек меняет его в панели области —
  // прежний `/farm` шёл за панелью, и раздел обязан тоже: иначе панель
  // говорит «Заря», а экран показывает «Зимовники». Выбор кластера или
  // компании раздел не трогает — хозяйством они не являются.
  // Вкладка переезжает вместе с человеком, фильтр (`issue`, `subtab`) — нет:
  // он про поля прежнего хозяйства.
  const followRef = useRef({ farmId, tab });
  followRef.current = { farmId, tab };
  useScopeChanged((detail) => {
    if (detail.scope_type !== 'farm' || !detail.scope_id) return;
    if (detail.scope_id === followRef.current.farmId) return;
    void navigate({
      to: '/farm/$farmId',
      params: { farmId: detail.scope_id },
      search: { tab: followRef.current.tab },
      replace: true,
    });
  });

  // Вкладка живёт в АДРЕСЕ, а не в состоянии компонента: F5, «назад» и
  // пересланная ссылка обязаны вернуть человека на ту вкладку, где он был.
  // Отсюда же берётся значение — локальная копия рано или поздно разошлась бы
  // с адресом (обещание в комментарии к роуту без этой навигации не работало).
  const activeTab = resolveFarmCardTab(tab);
  const setActiveTab = useCallback(
    (next: FarmCardTab) => {
      void navigate(farmCardTabNavigation(farmId, next));
    },
    [navigate, farmId],
  );
  // Клик по шагу полосы: адрес даёт сервер (`href_hint`). Вкладка этого же
  // хозяйства переключается без перезагрузки и без второго адреса в истории
  // (как любая вкладка); всё остальное (планирование) — обычный переход.
  const openTwinStep = useCallback(
    (step: TwinStep) => {
      if (!step.href) return;
      const target = farmTabFromHref(step.href, farmId);
      if (target) setActiveTab(resolveFarmCardTab(target));
      else void navigate({ href: step.href });
    },
    [farmId, navigate, setActiveTab],
  );
  const machineryView = resolveMachineryView(subtab);
  const setMachineryView = useCallback(
    (next: MachineryView) => {
      void navigate(
        farmCardTabNavigation(farmId, 'machinery', next === 'units' ? undefined : next),
      );
    },
    [navigate, farmId],
  );

  const [mode, setMode] = useState<FarmFieldsMode>('active');
  const [issue, setIssue] = useState<string | null>(issueFromUrl ?? null);
  const [search, setSearch] = useState('');
  const [brigade, setBrigade] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const fieldsQuery = useFarmFields(farmId, { mode, issue, brigade, search });
  const pages = fieldsQuery.data?.pages;
  // Итог («сколько всего») берём из ПЕРВОЙ страницы: бэкенд считает его по
  // всей выборке под фильтром, а не по отданным строкам.
  const total = pages?.[0]?.total ?? 0;
  const totalAreaHa = pages?.[0]?.total_area_ha ?? 0;
  const items = useMemo(() => pages?.flatMap((p) => p.items) ?? [], [pages]);

  // ── Диалоги ──────────────────────────────────────────────────────────────
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [brigadeDialog, setBrigadeDialog] = useState<{
    open: boolean;
    brigade: FarmBrigadeRow | null;
  }>({ open: false, brigade: null });
  const [requisitesOpen, setRequisitesOpen] = useState(false);

  // Реквизиты правит прежний drawer — второй формы под то же самое не заводим.
  const { data: clusters = [] } = useClusters();
  const { data: companies = [] } = useCompanies();
  const { data: farms = [] } = useFarms(false);
  const farmRow = farms.find((f) => f.id === farmId);

  const stats = useMemo(() => selectionStats(items, selected), [items, selected]);

  // Поиск с паузой. Без неё каждая буква уходила запросом на список И сбрасывала
  // отметки чекбоксов — человек терял выбор, пока дописывал слово. Хук общий с
  // экраном полей (AK-750), своего дребезга не изобретаем.
  const pushSearch = useCallback(
    (value: string) => {
      setSearch(value);
      setSelected(new Set());
    },
    [],
  );
  const [searchDraft, setSearchDraft] = useTextFilterDraft(search, pushSearch);

  const resetSelection = useCallback(() => setSelected(new Set()), []);

  /** Сменили выборку — прежний выбор относится к полям, которых на экране уже
   *  нет. Держать его значит архивировать вслепую. */
  const switchList = useCallback(
    (next: Partial<{ mode: FarmFieldsMode; issue: string | null; brigade: string | null; search: string }>) => {
      if (next.mode !== undefined) setMode(next.mode);
      if (next.issue !== undefined) setIssue(next.issue);
      if (next.brigade !== undefined) setBrigade(next.brigade);
      if (next.search !== undefined) {
        // Черновик поля ввода двигаем ВМЕСТЕ со значением запроса: иначе в
        // поиске осталось бы слово, которого фильтр уже не применяет, и
        // отложенный push вернул бы его обратно через 300 мс.
        setSearch(next.search);
        setSearchDraft(next.search);
      }
      resetSelection();
    },
    [resetSelection, setSearchDraft],
  );

  // Каждое массовое действие — ОДНА мутация с ОДНОЙ инвалидацией в onSettled
  // (канон «Массовые действия»): при частичном отказе сервер уже изменился, и
  // экран обязан показать факт независимо от исхода.
  const bulk = useFarmCardBulk(farmId, async (run: () => Promise<void>) => run());

  const runBulk = (
    action: () => Promise<{ kind: 'success' | 'error' | 'warning' | 'info'; message: string; doneCount: number }>,
    closeDialog: () => void,
  ) => {
    bulk.mutate(async () => {
      const outcome = await action();
      // ОДИН итог словами на всё действие, с именами полей и причинами.
      toast[outcome.kind](outcome.message);
      if (outcome.doneCount > 0) {
        resetSelection();
        // Поля ушли/вернулись на карту — сбрасываем chip-кеш карты.
        invalidateMapInit();
      }
      closeDialog();
    });
  };

  const saveBrigade = useSaveBrigade(farmId);

  if (summaryQuery.isLoading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-7xl p-4">
          <p className="text-sm text-muted-foreground">Загружаю данные хозяйства…</p>
        </div>
      </AppShell>
    );
  }

  if (summaryQuery.error || !summary) {
    return (
      <AppShell>
        <div className="mx-auto max-w-7xl p-4">
          <PageHeader title="Данные хозяйства" />
          <p className="text-sm text-destructive">
            Не удалось открыть данные хозяйства. Обновите страницу — если не поможет, выберите
            хозяйство заново в переключателе вверху меню.
          </p>
        </div>
      </AppShell>
    );
  }

  const note = contourNote(summary.arable_ha, summary.contour_arable_ha);
  // Форма сведений — прежний drawer «Структуры» (тот же редактор, что в
  // Компания → Структура), открытый на месте: уводить человека в дерево
  // искать то же хозяйство заново — лишний круг. Без строки хозяйства или
  // компании форме нечего открыть — и кнопки нет.
  const canEditDetails = !!farmRow && !!companies[0];

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl p-4">
        <Stack gap={4}>
          <PageHeader
            showBack={false}
            title={summary.name}
            description={farmHeaderLine(summary) || undefined}
            actions={
              canEditDetails ? (
                <Button variant="outline" onClick={() => setRequisitesOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Изменить сведения
                </Button>
              ) : undefined
            }
          />

          {prepQuery.data && (
            <FarmTwinStrip
              steps={twin.steps}
              doneCount={twin.doneCount}
              totalSteps={twin.totalSteps}
              dataAsOf={twin.lastUpdateAt}
              caption={`считается само · ${lastUpdateText(twin.lastUpdateAt)}`}
              onStepClick={openTwinStep}
            />
          )}

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(resolveFarmCardTab(v))}
          >
            {/* Шесть вкладок на телефоне не помещаются: ряд листается сам, а
                страница горизонтально не скроллится (макет v2, экран 5). */}
            <div className="max-w-full overflow-x-auto">
              <TabsList className="w-max justify-start">
                <TabsTrigger value="overview">Обзор</TabsTrigger>
                <TabsTrigger value="fields">Поля ({summary.fields_active})</TabsTrigger>
                <TabsTrigger value="brigades">
                  Отделения ({summary.brigades_count})
                </TabsTrigger>
                <TabsTrigger value="machinery">
                  Техника
                  {machineryCounts.data ? ` (${machineryCounts.data.total})` : ''}
                </TabsTrigger>
                <TabsTrigger value="people">Люди</TabsTrigger>
                <TabsTrigger value="history">История культур</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-4 pt-4">
              <TileGrid cols={4}>
                <Tile
                  label="Пашня, учётная"
                  value={formatHa(summary.arable_ha)}
                  hint={note ?? 'учётная и контурная сходятся'}
                />
                <Tile
                  label="Всего земли"
                  value={formatHa(summary.total_ha)}
                  hint="пашня + прочие угодья"
                />
                <Tile
                  label="Полей активных"
                  value={String(summary.fields_active)}
                  hint={`${summary.fields_archived} в архиве · ${summary.brigades_count} отделения`}
                />
                <Tile
                  label="Требуют разбора"
                  value={String(summary.health.reduce((sum, s) => sum + s.count, 0))}
                  hint="сигналы «здоровья данных»"
                />
              </TileGrid>
              <OverviewTab
                summary={summary}
                onOpenIssue={(nextIssue) => {
                  switchList({ mode: 'active', issue: nextIssue, brigade: null, search: '' });
                  setActiveTab('fields');
                }}
                onOpenPlan={() => void navigate({ to: '/planning' })}
              />
              {/* AK-938: блок «Опыты» хозяйства (low-fi 7·А). */}
              <FarmTrialsBlock farmId={farmId} canPlace={canWrite} />
            </TabsContent>

            <TabsContent value="fields" className="pt-4">
              <FieldsTab
                farmId={farmId}
                summary={summary}
                mode={mode}
                onModeChange={(next) => switchList({ mode: next, issue: null })}
                issue={issue}
                onClearIssue={() => switchList({ issue: null })}
                items={items}
                total={total}
                totalAreaHa={totalAreaHa}
                loading={fieldsQuery.isLoading}
                hasMore={fieldsQuery.hasNextPage}
                loadingMore={fieldsQuery.isFetchingNextPage}
                onLoadMore={() => void fieldsQuery.fetchNextPage()}
                batchMax={ARCHIVE_BATCH_MAX}
                search={searchDraft}
                onSearchChange={setSearchDraft}
                brigade={brigade}
                onBrigadeChange={(value) => switchList({ brigade: value })}
                selected={selected}
                onToggleRow={(id) =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })
                }
                // «Выбрать всё» упирается в предел ОДНОЙ пачки: ручка принимает
                // 200, а список к этому моменту мог догрузить больше — без
                // обрезки один клик давал бы 422 вместо ответа по существу.
                // Про обрезку человеку говорит подпись в панели выделения.
                onToggleAll={(ids, checked) =>
                  setSelected(checked ? new Set(capBatchSelection(ids)) : new Set())
                }
                canWrite={canWrite}
                canMove={canMoveToBrigade}
                busy={bulk.isPending}
                onArchive={() => setArchiveOpen(true)}
                onMove={() => setMoveOpen(true)}
                onRestore={() =>
                  runBulk(
                    () =>
                      restoreFields(
                        items.filter((r) => selected.has(r.id)).map((r) => r.id),
                        items,
                      ),
                    () => undefined,
                  )
                }
                onAddField={
                  canWrite
                    ? () => void navigate({ to: '/map', search: { addField: 1, farm: farmId } })
                    : undefined
                }
              />
            </TabsContent>

            <TabsContent value="brigades" className="pt-4">
              <BrigadesTab
                summary={summary}
                canWrite={canWrite}
                onCreate={() => setBrigadeDialog({ open: true, brigade: null })}
                onEdit={(b) => setBrigadeDialog({ open: true, brigade: b })}
                onOpenFields={(brigadeId) => {
                  switchList({ mode: 'active', issue: null, brigade: brigadeId ?? 'none' });
                  setActiveTab('fields');
                }}
              />
            </TabsContent>

            <TabsContent value="machinery" className="pt-4">
              <FarmMachinerySection
                farmId={farmId}
                farmName={farmRow?.name ?? summary.name}
                canWrite={canManageMachinery}
                counts={machineryCounts.data}
                view={machineryView}
                onViewChange={setMachineryView}
                openImport={action === 'import'}
              />
            </TabsContent>

            <TabsContent value="people" className="pt-4">
              {/* Честное пустое состояние: людей хозяйства в системе ещё нет
                  (вкладка делается в AK-901). Без «скоро» и без номеров задач. */}
              <Card data-testid="people-empty">
                <CardContent className="p-4 text-sm text-muted-foreground">
                  Люди хозяйства появятся вместе с механизаторами и закреплением полей.
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="pt-4">
              {/* Неактивная вкладка не монтируется (Radix) — матрица 7 лет
                  не грузится ради «Обзора». */}
              <FarmCropHistoryMatrix farmId={farmId} />
            </TabsContent>
          </Tabs>
        </Stack>
      </div>

      <ArchiveFieldsDialog
        open={archiveOpen}
        stats={stats}
        pending={bulk.isPending}
        onCancel={() => setArchiveOpen(false)}
        onConfirm={(reason) =>
          runBulk(
            () =>
              archiveFields(
                items.filter((r) => selected.has(r.id)).map((r) => r.id),
                reason,
                items,
              ),
            () => setArchiveOpen(false),
          )
        }
      />

      <MoveToBrigadeDialog
        open={moveOpen}
        stats={stats}
        brigades={summary.brigades}
        pending={bulk.isPending}
        onCancel={() => setMoveOpen(false)}
        onConfirm={(brigadeId) =>
          runBulk(
            () =>
              moveFieldsToBrigade(
                farmId,
                items.filter((r) => selected.has(r.id)).map((r) => r.id),
                brigadeId,
                items,
              ),
            () => setMoveOpen(false),
          )
        }
      />

      <BrigadeDialog
        open={brigadeDialog.open}
        brigade={brigadeDialog.brigade}
        pending={saveBrigade.isPending}
        onCancel={() => setBrigadeDialog({ open: false, brigade: null })}
        onSave={(draft: BrigadeDraft) =>
          saveBrigade.mutate(
            { brigadeId: brigadeDialog.brigade?.id ?? null, payload: draft },
            {
              onSuccess: () => toast.success(`Отделение «${draft.name}» сохранено`),
              onError: () => toast.error('Не удалось сохранить отделение'),
              onSettled: () => setBrigadeDialog({ open: false, brigade: null }),
            },
          )
        }
      />

      {farmRow && companies[0] && (
        <FarmDrawer
          open={requisitesOpen}
          onOpenChange={setRequisitesOpen}
          companyId={companies[0].id}
          clusters={clusters}
          // Drawer типизирован под строку ДЕРЕВА (`FarmWithAggregate`), где
          // агрегаты приезжают из `/farms/bbox`. Карточке они не нужны — её
          // цифры считает сводка, и они точнее (учётная мера, AK-751/752).
          // Поэтому агрегаты для формы реквизитов заполняем сводкой, а не
          // тащим ради drawer'а ещё один запрос.
          farm={{
            ...farmRow,
            field_count: summary.fields_active,
            area_ha: summary.total_ha,
            archived: farmRow.archived_at != null,
          }}
        />
      )}
    </AppShell>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <span className="block text-xs uppercase text-muted-foreground">{label}</span>
        <span className="block text-xl font-semibold tabular-nums">{value}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </CardContent>
    </Card>
  );
}
